"use client";

// src/components/analytics/GAPageviewTracker.tsx
//
// GA4のページビュー自動計測（App Router対応）。
//
// 問題：Next.js App Routerはクライアント側ルーティング（フルページリロード
// なし）のため、gtag.js標準のページビュー自動計測は初回読み込み時にしか
// 発火しない。素朴に埋め込むと2ページ目以降のページビューが一切記録
// されない、という既知の落とし穴がある。
//
// 対応：usePathname / useSearchParams でルート変化を検知し、変化のたびに
// 手動で pageview() を呼ぶ。useSearchParams を使うコンポーネントは
// Suspense境界の内側に置く必要がある（呼び出し元の ClientLayout.tsx が
// 既に <Suspense> で包んでいるため、そのまま追加できる）。
//
// 表示は行わない（null を返すだけ）。ルーティング監視専用のコンポーネント。

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { pageview, GA_MEASUREMENT_ID } from "@/lib/gtag";

export default function GAPageviewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!GA_MEASUREMENT_ID) return;

    // 2026-10-13・CEO室依頼：/admin配下は計測しない。
    // GA公式の無効化フラグ（ga-disable-<測定ID>）で、ページビューだけでなく
    // 拡張計測（スクロール等）を含むすべての送信を止める。admin以外へ遷移したら戻す。
    const disabled = pathname.startsWith("/admin");
    (window as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = disabled;
    if (disabled) return;

    // 2026-10-13・個人情報対策：検索部分（?以降）には、交換コード（/redeem?code=）や
    // ユーザーID等が含まれ得る。GA4へは、流入元の分析に必要なもの（utm_*・ref）だけを送る。
    const kept = new URLSearchParams();
    searchParams.forEach((value, key) => {
      if (key.startsWith("utm_") || key === "ref") kept.append(key, value);
    });
    const query = kept.toString();
    pageview(query ? `${pathname}?${query}` : pathname);
  }, [pathname, searchParams]);

  return null;
}
